import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BranchPicker from "../components/BranchPicker";
import DisposalTab from "./Disposal";

const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]";
const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

function CostPerKm({ branchId }) {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const load = () => api.get("/accounts/cost-per-km", { params: { branch_id: branchId || undefined, date_from: from || undefined, date_to: to || undefined } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [branchId]);
  if (!data) return <div className="mt-6 text-sm text-slate-400">Loading…</div>;
  return (
    <div className="mt-4">
      <div className="flex gap-2 items-end flex-wrap">
        <div><label className="text-[10px] uppercase tracking-wider text-slate-400">From</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} /></div>
        <div><label className="text-[10px] uppercase tracking-wider text-slate-400">To</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} /></div>
        <button data-testid="cpk-apply-btn" onClick={load} className="rounded-xl bg-[#0E2240] px-4 py-2.5 text-sm font-bold text-white">Apply</button>
      </div>
      <p className="mt-2 text-[11px] text-slate-400">Period: {data.date_from} → {data.date_to} · Cost/km = (EMI + fuel + repair + other) ÷ km clocked</p>
      <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
            <tr>{["Vehicle", "Km", "TDs", "EMI", "Fuel", "Repair", "Other", "Total", "₹/km", "EMI/km", "₹/TD", ""].map((h) => <th key={h} className="px-3 py-2.5 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.vehicle_id} data-testid={`cpk-row-${r.reg_no}`} className={`border-t border-slate-100 ${r.under_utilized ? "bg-red-50" : ""}`}>
                <td className="px-3 py-2.5 font-bold text-[#0E2240] whitespace-nowrap">{r.reg_no}<div className="font-normal text-slate-400">{r.model}</div></td>
                <td className="px-3 py-2.5">{r.km.toLocaleString("en-IN")}</td>
                <td className="px-3 py-2.5">{r.test_drives}</td>
                <td className="px-3 py-2.5">{inr(r.emi)}</td>
                <td className="px-3 py-2.5">{inr(r.fuel)}</td>
                <td className="px-3 py-2.5">{inr(r.repair)}</td>
                <td className="px-3 py-2.5">{inr(r.other)}</td>
                <td className="px-3 py-2.5 font-bold">{inr(r.total)}</td>
                <td className="px-3 py-2.5 font-bold text-[#BB162B]">{r.cost_per_km ? `₹${r.cost_per_km}` : "—"}</td>
                <td className="px-3 py-2.5">{r.emi_per_km ? `₹${r.emi_per_km}` : "—"}</td>
                <td className="px-3 py-2.5">{r.cost_per_td ? inr(r.cost_per_td) : "—"}</td>
                <td className="px-3 py-2.5">{r.under_utilized && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-bold text-red-700 whitespace-nowrap">UNDER-UTILIZED</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Ledger() {
  const [vehicles, setVehicles] = useState([]);
  const [vid, setVid] = useState("");
  const [ledger, setLedger] = useState(null);
  useEffect(() => { api.get("/vehicles").then((r) => { setVehicles(r.data); if (r.data[0]) setVid(r.data[0].id); }); }, []);
  useEffect(() => { if (vid) api.get("/accounts/ledger", { params: { vehicle_id: vid } }).then((r) => setLedger(r.data)); }, [vid]);
  const srcCls = { fuel: "bg-blue-100 text-blue-700", workshop: "bg-red-100 text-red-700", emi: "bg-purple-100 text-purple-700", other: "bg-amber-100 text-amber-700" };
  return (
    <div className="mt-4">
      <select data-testid="ledger-vehicle-select" value={vid} onChange={(e) => setVid(e.target.value)} className={inputCls}>
        {vehicles.map((v) => <option key={v.id} value={v.id}>{v.reg_no} — {v.model}</option>)}
      </select>
      {ledger && (
        <>
          <div className="mt-3 rounded-xl bg-[#0E2240] p-4 text-white">
            <div className="text-[10px] uppercase tracking-widest text-[#BF9B30]">Total spend (all time)</div>
            <div className="font-heading text-2xl font-bold">{inr(ledger.total)}</div>
          </div>
          <div className="mt-3 space-y-2">
            {ledger.entries.map((e, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-sm">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${srcCls[e.source]}`}>{e.category}</span>
                    <span className="text-[11px] text-slate-400">{e.date}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{e.note}</div>
                </div>
                <div className="font-bold text-[#0E2240]">{inr(e.amount)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EMIMaster({ canEdit }) {
  const [emis, setEmis] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ vehicle_id: "", monthly_emi_amount: "", financier: "", loan_start_date: "", tenure_months: "36", mode: "emi" });
  const load = () => { api.get("/accounts/emi").then((r) => setEmis(r.data)); api.get("/vehicles").then((r) => setVehicles(r.data)); };
  useEffect(load, []);
  const submit = async () => {
    try {
      await api.post("/accounts/emi", { ...f, monthly_emi_amount: parseFloat(f.monthly_emi_amount), tenure_months: parseInt(f.tenure_months) });
      toast.success("EMI record saved — accruals posted"); setShow(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const closeEmi = async (e) => {
    try {
      await api.post(`/accounts/emi/${e.vehicle_id}/close`, { closure_date: new Date().toISOString().slice(0, 10), kind: "closed" });
      toast.success(`EMI closed for ${e.reg_no}`); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="mt-4">
      {canEdit && <button data-testid="emi-add-btn" onClick={() => setShow(!show)} className="rounded-full bg-[#BB162B] px-4 py-2 text-sm font-bold text-white">+ EMI Record</button>}
      {show && (
        <div className="mt-3 space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
          <select data-testid="emi-vehicle-select" value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })} className={inputCls}>
            <option value="">Select vehicle…</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.reg_no} — {v.model}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="emi-amount-input" type="number" value={f.monthly_emi_amount} onChange={(e) => setF({ ...f, monthly_emi_amount: e.target.value })} placeholder="Monthly EMI ₹ (0 if owned)" className={inputCls} />
            <input value={f.financier} onChange={(e) => setF({ ...f, financier: e.target.value })} placeholder="Financier" className={inputCls} />
            <input type="date" value={f.loan_start_date} onChange={(e) => setF({ ...f, loan_start_date: e.target.value })} className={inputCls} />
            <input type="number" value={f.tenure_months} onChange={(e) => setF({ ...f, tenure_months: e.target.value })} placeholder="Tenure (months)" className={inputCls} />
            <select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} className={inputCls}>
              <option value="emi">EMI</option><option value="depreciation">Notional depreciation</option>
            </select>
          </div>
          <button data-testid="emi-submit-btn" onClick={submit} className="w-full rounded-xl bg-[#0E2240] py-3 text-sm font-bold text-white">Save</button>
        </div>
      )}
      <div className="mt-3 space-y-2">
        {emis.map((e) => (
          <div key={e.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-sm">
            <div>
              <div className="font-bold text-[#0E2240] text-[13px]">{e.reg_no}{e.active === false && <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-[9px] font-bold uppercase text-slate-500">{e.closure_kind || "closed"}</span>}</div>
              <div className="text-[11px] text-slate-400">{e.financier} · {e.loan_start_date} → {e.loan_end_date} · {e.mode === "depreciation" ? "Depreciation" : "EMI"}</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="font-bold text-[#0E2240]">{inr(e.monthly_emi_amount)}/mo</div>
              {canEdit && e.active !== false && (
                <button data-testid={`emi-close-btn-${e.reg_no}`} onClick={() => closeEmi(e)}
                  className="rounded-full border border-slate-300 px-2.5 py-1 text-[10px] font-bold text-slate-500">Close</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Expenses({ canEdit }) {
  const [vehicles, setVehicles] = useState([]);
  const [f, setF] = useState({ vehicle_id: "", date: "", category: "insurance", amount: "", note: "" });
  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)); }, []);
  const submit = async () => {
    try {
      await api.post("/accounts/expenses", { ...f, amount: parseFloat(f.amount) });
      toast.success("Expense recorded"); setF({ ...f, amount: "", note: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!canEdit) return <div className="mt-6 text-sm text-slate-400">Only Accounts users can add other expenses.</div>;
  return (
    <div className="mt-4 space-y-3 rounded-2xl border border-slate-200 bg-white p-4 max-w-md">
      <select data-testid="expense-vehicle-select" value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })} className={inputCls}>
        <option value="">Select vehicle…</option>
        {vehicles.map((v) => <option key={v.id} value={v.id}>{v.reg_no} — {v.model}</option>)}
      </select>
      <div className="grid grid-cols-2 gap-2">
        <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} />
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inputCls}>
          {["insurance", "road_tax", "detailing", "accessories", "tolls_fines", "misc"].map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
        </select>
        <input data-testid="expense-amount-input" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Amount ₹" className={inputCls} />
        <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note" className={inputCls} />
      </div>
      <button data-testid="expense-submit-btn" onClick={submit} className="w-full rounded-xl bg-[#0E2240] py-3 text-sm font-bold text-white">Add Expense</button>
    </div>
  );
}

function Summary() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/accounts/summary").then((r) => setData(r.data)); }, []);
  if (!data) return null;
  return (
    <div className="mt-4">
      <div className="rounded-xl bg-[#0E2240] p-4 text-white">
        <div className="text-[10px] uppercase tracking-widest text-[#BF9B30]">Fleet outflow · {data.month}</div>
        <div className="font-heading text-3xl font-bold">{inr(data.total)}</div>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        {data.branches.map((b) => (
          <div key={b.branch_id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="font-heading font-bold text-[#0E2240]">{b.branch}</div>
            <div className="font-heading text-xl font-bold text-[#BB162B]">{inr(b.total)}</div>
            <div className="mt-1 text-[11px] text-slate-500">EMI {inr(b.emi)} · Fuel {inr(b.fuel)} · Repair {inr(b.repair)} · Other {inr(b.other)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Accounts() {
  const { user } = useAuth();
  const [tab, setTab] = useState("cpk");
  const [branchId, setBranchId] = useState(null);
  const canEdit = ["aho", "azh", "admin"].includes(user.role);
  const seesDisposal = ["aho", "azh", "vp", "md", "admin"].includes(user.role);
  const tabs = [["cpk", "Cost / Km"], ["summary", "Summary"], ["ledger", "Ledger"], ["emi", "EMI Master"], ["expense", "Expenses"],
    ...(seesDisposal ? [["disposal", "Disposal Fleet"]] : [])];
  return (
    <div className="px-4 py-5 lg:px-8 max-w-6xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-heading text-2xl font-bold text-[#0E2240]">Accounts</h1>
        <BranchPicker value={branchId} onChange={setBranchId} />
      </div>
      <div className="mt-4 flex gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1">
        {tabs.map(([t, l]) => (
          <button key={t} data-testid={`accounts-tab-${t}`} onClick={() => setTab(t)}
            className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-bold ${tab === t ? "bg-[#0E2240] text-white" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      {tab === "cpk" && <CostPerKm branchId={branchId} />}
      {tab === "summary" && <Summary />}
      {tab === "ledger" && <Ledger />}
      {tab === "emi" && <EMIMaster canEdit={canEdit} />}
      {tab === "expense" && <Expenses canEdit={canEdit} />}
      {tab === "disposal" && <DisposalTab />}
    </div>
  );
}
